/**
 * Shared readiness checks for campaign Active / enrol toggles.
 * Settings 7-Day and Custom Campaigns use the same enable/disable rule.
 */

export type DayIssue = { severity: 'error' | 'warning'; label: string };

/** Structural fields needed for connector readiness (no UI copy). */
export type DayConfigFields = {
  kind: string;
  hasText: boolean;
  hasImage: boolean;
  hasVoice: boolean;
  autoImageSource?: string;
};

export type EngagementDayLike = {
  dayNumber: number;
  enabled: boolean;
  whatsapp: {
    enabled: boolean;
    message: string;
    mediaUrl: string | null;
  };
};

export type MetaTemplateLike = {
  dayNumber: number;
  status: string | null;
};

export type AgentSettingsLike = {
  hasCalendlyKey?: boolean;
  calendlyEventTypeUri?: string | null;
} | null;

export type CampaignReadiness = {
  canActivate: boolean;
  reasons: string[];
};

/** One day that must be sendable before the campaign Active toggle can turn ON. */
export type CampaignActiveDay = {
  /** False when connector-side setup is incomplete (media, Calendly, message). */
  connectorOk?: boolean;
  /** Meta template status; required APPROVED when Official API is active. */
  metaStatus?: string | null;
};

/** Fixed 7-Day day shapes used for connectorIssue outside Settings. */
export const ENGAGEMENT_DAY_CONFIGS: DayConfigFields[] = [
  { kind: 'text', hasText: true, hasImage: false, hasVoice: false },
  { kind: 'image', hasText: true, hasImage: true, hasVoice: false },
  { kind: 'text_link', hasText: true, hasImage: false, hasVoice: false },
  { kind: 'text_image', hasText: true, hasImage: true, hasVoice: false, autoImageSource: 'picker_sales_update' },
  { kind: 'voice', hasText: false, hasImage: false, hasVoice: true },
  { kind: 'event', hasText: true, hasImage: false, hasVoice: false },
  { kind: 'image', hasText: true, hasImage: true, hasVoice: false },
];

const META_STATUS_LABELS: Record<string, string> = {
  PENDING: 'In review',
  IN_APPEAL: 'In appeal',
  PENDING_DELETION: 'Deleting',
};

function dayRequiresManualMedia(cfg: DayConfigFields): boolean {
  return (cfg.hasImage && !cfg.autoImageSource) || cfg.hasVoice;
}

/** Missing media, Calendly, or message — connector-side setup, not Meta. */
export function connectorIssue(
  day: EngagementDayLike,
  cfg: DayConfigFields,
  agentSettings: AgentSettingsLike,
  usingCloud: boolean,
): DayIssue | null {
  if (dayRequiresManualMedia(cfg) && !day.whatsapp.mediaUrl?.trim()) {
    return { severity: 'error', label: cfg.hasVoice ? 'Record voice note' : 'Upload image' };
  }
  if (cfg.kind === 'event' && !(agentSettings?.hasCalendlyKey && agentSettings?.calendlyEventTypeUri)) {
    return { severity: 'error', label: 'Connect Calendly' };
  }
  if (!day.whatsapp.message.trim() && (cfg.hasText || usingCloud)) {
    return { severity: 'error', label: 'Write message' };
  }
  return null;
}

/** Meta template approval state for one day. Null when not on the Official API. */
export function metaTemplateIssue(
  usingCloud: boolean,
  templateState: MetaTemplateLike | null,
): DayIssue | null {
  if (!usingCloud) return null;
  const metaStatus = templateState?.status?.toUpperCase();
  if (metaStatus === 'REJECTED') return { severity: 'error', label: 'Rejected' };
  if (metaStatus === 'PAUSED' || metaStatus === 'DISABLED') {
    return { severity: 'error', label: 'Blocked' };
  }
  if (metaStatus === 'PENDING' || metaStatus === 'IN_APPEAL' || metaStatus === 'PENDING_DELETION') {
    return {
      severity: 'warning',
      label: META_STATUS_LABELS[metaStatus] || metaStatus,
    };
  }
  if (!templateState?.status) return { severity: 'warning', label: 'Not created' };
  return null;
}

export function metaTemplateApproved(
  usingCloud: boolean,
  templateState: MetaTemplateLike | null,
): boolean {
  if (!usingCloud) return true;
  return (templateState?.status || '').toUpperCase() === 'APPROVED';
}

/**
 * Shared Active/Running toggle rule for 7-Day and Custom Campaigns:
 * - Always allow turning OFF
 * - Allow ON only when every sendable day is connector-ready
 * - On Official API, every sendable day must also be Meta APPROVED
 */
export function evaluateCampaignActiveReadiness(params: {
  usingCloud: boolean;
  days: CampaignActiveDay[];
}): CampaignReadiness {
  const reasons: string[] = [];
  let connectorFail = false;
  let metaFail = false;

  for (const d of params.days) {
    if (d.connectorOk === false) connectorFail = true;
    if (params.usingCloud && (d.metaStatus || '').toUpperCase() !== 'APPROVED') {
      metaFail = true;
    }
  }

  if (connectorFail) reasons.push('A day is not ready in Connector');
  if (metaFail) reasons.push('A day is not set up in Meta');
  return { canActivate: reasons.length === 0, reasons };
}

/**
 * 7-Day campaign / per-client engagement — same Active rule as custom campaigns.
 */
export function evaluateSevenDayReadiness(params: {
  days: EngagementDayLike[];
  configs?: DayConfigFields[];
  agentSettings: AgentSettingsLike;
  usingCloud: boolean;
  templates: MetaTemplateLike[];
}): CampaignReadiness {
  const { days, agentSettings, usingCloud, templates } = params;
  const configs = params.configs || ENGAGEMENT_DAY_CONFIGS;
  const activeDays: CampaignActiveDay[] = [];

  for (let i = 0; i < Math.min(days.length, configs.length); i++) {
    const day = days[i];
    const cfg = configs[i];
    const template = templates.find((t) => t.dayNumber === day.dayNumber) ?? null;
    activeDays.push({
      connectorOk: !connectorIssue(day, cfg, agentSettings, usingCloud),
      metaStatus: template?.status ?? null,
    });
  }

  return evaluateCampaignActiveReadiness({ usingCloud, days: activeDays });
}

/**
 * Custom campaign Active — same rule as 7-Day, for every day in the campaign.
 * Empty slots are not ready. On the bridge, every day must be connector-ready.
 * On the Official API, every day must be connector-ready and Meta APPROVED.
 * Connector: a day needs content, and a Calendly day needs a connected account.
 */
export function evaluateCustomCampaignReadiness(params: {
  usingCloud: boolean;
  days: Array<{
    dayNumber?: number;
    status?: string | null;
    hasContent: boolean;
    useCalendly?: boolean;
  }>;
  /** Campaign length. Missing slots count as not ready. */
  numDays?: number;
  hasCalendly?: boolean;
}): CampaignReadiness {
  const byNumber = new Map<number, (typeof params.days)[number]>();
  params.days.forEach((d, i) => {
    byNumber.set(d.dayNumber ?? i + 1, d);
  });
  const count = params.numDays ?? byNumber.size;
  if (count < 1) {
    return { canActivate: false, reasons: ['A day is not ready in Connector'] };
  }
  const slots: CampaignActiveDay[] = [];
  for (let n = 1; n <= count; n++) {
    const d = byNumber.get(n);
    const hasContent = !!d?.hasContent;
    const calendlyOk = !d?.useCalendly || !!params.hasCalendly;
    slots.push({
      connectorOk: hasContent && calendlyOk,
      metaStatus: d?.status ?? null,
    });
  }
  return evaluateCampaignActiveReadiness({ usingCloud: params.usingCloud, days: slots });
}

/** Whether a custom-campaign DB row has sendable content. */
export function campaignRowHasContent(d: {
  MessageTemplate?: string | null;
  ImageUrl?: string | null;
  VideoUrl?: string | null;
  DocumentUrl?: string | null;
  VoiceUrl?: string | null;
  LinkUrl?: string | null;
  UseCalendly?: boolean | null;
  MediaUrl?: string | null;
  mediaIds?: string[] | null;
  media?: unknown[] | null;
}): boolean {
  return !!(
    (d.MessageTemplate || '').trim()
    || d.ImageUrl
    || d.VideoUrl
    || d.DocumentUrl
    || d.VoiceUrl
    || d.MediaUrl
    || (d.LinkUrl || '').trim()
    || d.UseCalendly
    || (d.mediaIds && d.mediaIds.length > 0)
    || (d.media && d.media.length > 0)
  );
}

/** Build engagement day shapes from `/campaigns` WhatsApp rows. */
export function engagementDaysFromCampaignRows(
  rows: Array<{
    DayNumber: number;
    Channel: string;
    MessageTemplate?: string | null;
    MediaUrl?: string | null;
    IsActive?: boolean | null;
  }>,
): EngagementDayLike[] {
  const days: EngagementDayLike[] = Array.from({ length: 7 }, (_, i) => ({
    dayNumber: i + 1,
    enabled: i === 4,
    whatsapp: { enabled: true, message: '', mediaUrl: null },
  }));
  for (const d of rows) {
    if (d.Channel !== 'whatsapp') continue;
    const idx = d.DayNumber - 1;
    if (idx < 0 || idx >= 7) continue;
    const active = d.IsActive !== false;
    days[idx].enabled = active;
    days[idx].whatsapp = {
      enabled: active,
      message: d.MessageTemplate || '',
      // Day 4 uses auto weekly image — MediaUrl is cleared in Settings too
      mediaUrl: d.DayNumber === 4 ? null : (d.MediaUrl || null),
    };
  }
  return days;
}

export function readinessWarningText(reasons: string[]): string {
  return reasons.join(' · ');
}
